// components/tracking/MetaPixelTracker.tsx
'use client';

import { useEffect } from 'react';
import { useConsentCheck } from '@/app/hooks/useConsentCheck';

interface MetaPixelTrackerProps {
  eventType: 'ViewContent' | 'Purchase'; // Nur diese zwei Events werden hier behandelt
  contentIds: string[];
  value?: number;     // Wird als number | undefined von ClientSideTracking übergeben
  currency?: string;  // Wird als string | undefined übergeben (oder default 'EUR')
  eventId?: string;
}

export default function MetaPixelTracker({
                                           eventType,
                                           contentIds,
                                           value, // Ist bereits number | undefined
                                           currency = 'EUR', // Default EUR
                                           eventId
                                         }: MetaPixelTrackerProps) {
  const { hasConsent } = useConsentCheck('facebook');

  useEffect(() => {
    // Frühzeitiger Ausstieg, wenn kein Fensterobjekt oder keine Zustimmung vorhanden ist
    if (typeof window === 'undefined' || !hasConsent || !window.fbq) {
      // window.fbq Check hinzugefügt, um sicherzustellen, dass der Basis-Pixel geladen ist
      if (hasConsent && typeof window !== 'undefined' && !window.fbq) {
        console.warn('MetaPixelTracker: Consent given, but window.fbq not found. Meta Pixel base code might be missing or loaded too late.');
      }
      return;
    }


    // Die eigentliche Tracking Logik (ggf. ohne setTimeout, wenn nicht unbedingt nötig)
    const trackNow = () => {
      console.log(`MetaPixelTracker: Attempting to track ${eventType}`);

      try { // Füge try-catch hinzu, um Fehler im Tracking abzufangen
        if (eventType === 'ViewContent') {
          // Prüfen ob die (potenziell benutzerdefinierte) Wrapper-Funktion existiert
          if (window.trackViewContent) {
            // Prüfen, ob Wert eine gültige Zahl ist und Währung existiert
            if (typeof value === 'number' && !isNaN(value) && currency) {
              console.log('MetaPixelTracker (via trackViewContent): Tracking ViewContent with value/currency:', { contentIds, contentType: 'product', value, currency, eventId });
              window.trackViewContent(contentIds, 'product', value, currency, eventId);
            } else {
              console.log('MetaPixelTracker (via trackViewContent): Tracking ViewContent without value/currency:', { contentIds, contentType: 'product', eventId });
              // Rufe ohne value/currency auf, stelle sicher, dass die Reihenfolge stimmt
              // und übergebe explizit undefined, um die Signatur zu matchen
              window.trackViewContent(contentIds, 'product', undefined, undefined, eventId);
            }
          } else if (window.fbq) { // Fallback auf Standard fbq, falls trackViewContent fehlt
            console.warn('MetaPixelTracker: window.trackViewContent not found, using window.fbq directly.');
            const params: { content_ids: string[]; content_type: string; value?: number; currency?: string; event_id?: string } = {
              content_ids: contentIds,
              content_type: 'product',
            };
            if (typeof value === 'number' && !isNaN(value) && currency) {
              params.value = value;
              params.currency = currency;
            }
            if (eventId) {
              params.event_id = eventId;
            }
            console.log('MetaPixelTracker (via fbq): Tracking ViewContent with params:', params);
            window.fbq('track', 'ViewContent', params, { eventID: eventId }); // eventID hier für Deduplizierung
          } else {
            console.error('MetaPixelTracker: Neither window.trackViewContent nor window.fbq available.');
          }

        } else if (eventType === 'Purchase') {
          // Prüfen ob die (potenziell benutzerdefinierte) Wrapper-Funktion existiert
          if (window.trackPurchase) {
            if (typeof value === 'number' && !isNaN(value) && currency) {
              console.log('MetaPixelTracker (via trackPurchase): Tracking Purchase:', { contentIds, value, currency, contentType: 'product', eventId });
              window.trackPurchase(contentIds, value, currency, 'product', eventId);
            } else {
              console.error('MetaPixelTracker (via trackPurchase): Purchase event requires a valid value and currency.', { contentIds, value, currency });
            }
          } else if (window.fbq) { // Fallback auf Standard fbq
            console.warn('MetaPixelTracker: window.trackPurchase not found, using window.fbq directly.');
            if (typeof value === 'number' && !isNaN(value) && currency) {
              const params = {
                content_ids: contentIds,
                content_type: 'product',
                value: value,
                currency: currency,
                // event_id: eventId // event_id ist kein Standardparameter für fbq Purchase, wird über options gesetzt
              };
              console.log('MetaPixelTracker (via fbq): Tracking Purchase with params:', params);
              window.fbq('track', 'Purchase', params, { eventID: eventId }); // eventID hier für Deduplizierung
            } else {
              console.error('MetaPixelTracker (via fbq): Purchase event requires a valid value and currency.', { contentIds, value, currency });
            }
          } else {
            console.error('MetaPixelTracker: Neither window.trackPurchase nor window.fbq available.');
          }
        }
      } catch (error) {
        console.error(`MetaPixelTracker: Error during tracking event ${eventType}:`, error);
      }
    };

    // setTimeout entfernt - löse Tracking direkt aus oder nur mit minimaler Verzögerung, falls absolut nötig.
    // Eine lange Verzögerung erhöht das Risiko, dass der User die Seite verlässt, bevor das Event gesendet wird.
    // const timer = setTimeout(trackNow, 50); // Kurze Verzögerung, falls nötig
    trackNow(); // Direkter Aufruf

    // return () => clearTimeout(timer); // Nur wenn setTimeout verwendet wird

  }, [eventType, contentIds, value, currency, eventId, hasConsent]); // Abhängigkeiten prüfen

  return null; // Diese Komponente rendert nichts
}